import {
  ArrowCircleUpOutlined,
  KeyOffOutlined,
  KeyOutlined,
  Person4Outlined,
} from "@mui/icons-material";
import { Box, Button, Divider, Grid, Paper, Typography } from "@mui/material";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserActions from "../../../../redux/users/users.actions";
import * as userReducer from "../../../../redux/users/users.reducers";
import brand, { primary, secondary } from "../../../../themes/theme";
import MyTextField from "../../../../utils/common/my-textfield/MyTextField";
import { RevealFade } from "../../../../utils/Reveal";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import LoginUI from "../../components/login-ui/LoginUI";
import LoginUIXS from "../../components/login-ui/LoginUIXS";

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .trim()
    .matches(
      /^[^\s].{2,23}[^\s]$/,
      "Name must be between 4 and 25 characters without leading or trailing spaces"
    )
    .required("Name is required"),
  email: Yup.string()
    .trim()
    .matches(
      /^[^\s].{2,30}[^\s]$/,
      "Email must be between 4 and 30 characters without leading or trailing spaces"
    )
    .required("Email is required"),
  password: Yup.string()
    .trim()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*()\-=_+{}[\]:;"'<>,.?/~`|]).{6,15}$/,
      "Password must be between 6 and 15 characters, and include at least one uppercase letter, one lowercase letter, one number, and one special character"
    )
    .required("Password is required"),
});

const UserRegister: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  //Get data from redux store
  const userState: userReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[userReducer.userFeatureKey];
    }
  );

  const { loading } = userState;

  const handleAddUser = async (values: any) => {
    dispatch(UserActions.registerUserAction({ user: values })).then(
      (response: any) => {
        if (response && !response.error) {
          navigate("/users/login");
        }
      }
    );
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleAddUser(values);
      console.log(values);
      // formik.resetForm();
    },
  });

  return (
    <>
      {loading && <SpinnerUI />}
      <Box>
        <MainNavBar />
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "center",
            height: { xs: "80vh", md: "100vh" },
            alignItems: "center",
            alignContent: "center",
            width: "100%",
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            sx={{
              background: secondary[50],
              width: "100%",
              display: { xs: "none", md: "flex" },
              height: "100%",
            }}
          >
            <LoginUI
              id="register"
              title="Create Account"
              description="Join us in just moments with HUM."
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            sx={{
              background: secondary[50],
              width: "100%",
              display: { xs: "flex", md: "none" },
            }}
          >
            <LoginUIXS
              id="register"
              title="Create Account"
              description="Join us in just moments with HUM."
            />
          </Grid>
          <Grid item xs={11} sm={11} md={4}>
            <RevealFade>
              <Box
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: "10px",
                  pt: 3,
                  background: secondary[300],
                  width: { xs: "100%", sm: "333px", md: "350px" },
                  maxWidth: "500px",
                  minWidth: "100%",
                }}
              >
                <Typography
                  sx={{
                    textAlign: "left",
                    fontFamily: "Poppins",
                    fontWeight: "bold",
                    color: primary[700],
                    fontSize: "22px",
                    pl: 3,
                  }}
                >
                  Register Here!
                </Typography>
                <Typography
                  sx={{
                    textAlign: "left",
                    fontFamily: "Poppins",
                    fontWeight: "500",
                    color: primary[400],
                    fontSize: "12px",
                    pl: 3,
                    maxWidth: "300px",
                    width: "100%",
                  }}
                >
                  We are excited to have you join us. Please register to
                  continue.
                </Typography>
                <form onSubmit={formik.handleSubmit}>
                  <MyTextField
                    formik={formik}
                    name="username"
                    type="text"
                    placeholder="Username"
                    icon={
                      <Person4Outlined fontSize="small" sx={{ mb: "5px" }} />
                    }
                  />
                  <MyTextField
                    formik={formik}
                    name="email"
                    type="email"
                    placeholder="Email"
                    icon={"@"}
                  />
                  <MyTextField
                    formik={formik}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    icon={
                      showPassword ? (
                        <>
                          <KeyOutlined
                            onClick={handleTogglePassword}
                            fontSize="small"
                            sx={{ mb: "5px", cursor: "pointer" }}
                          />
                        </>
                      ) : (
                        <KeyOffOutlined
                          onClick={handleTogglePassword}
                          fontSize="small"
                          sx={{ mb: "5px", cursor: "pointer" }}
                        />
                      )
                    }
                  />

                  <Box sx={{ mt: 3, px: 3 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        py: 1,
                        borderRadius: "10px",
                        background: brand[900],
                        color: secondary[50],
                        filter: 'brightness(100%)',
                        boxShadow:0,
                        "&:hover": {
                          background: brand[900],
                          color: secondary[50],
                          filter: 'brightness(110%)',
                          boxShadow:0,
                          transition:'1s ease-in-out'
                        },
                      }}
                    >
                      <ArrowCircleUpOutlined
                        fontSize="small"
                        sx={{ mr: "5px" }}
                      />{" "}
                      Register
                    </Button>
                  </Box>
                </form>
                <Divider variant="middle" sx={{ mt: 3 }} />
                <Grid
                  container
                  sx={{
                    py: 2,
                    display: "flex",
                    justifyContent: "center",
                    px: 3,
                  }}
                >
                  <Grid item xs={12} sm={12}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "Ubuntu", textAlign: "left" }}
                    >
                      Already have an account?
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => navigate("/users/login")}
                      sx={{
                        display: "flex",
                        background: "transparent",
                        "&:hover": { background: "transparent" },
                        textTransform: "capitalize",
                        justifyContent: "flex-start",
                        height: "100%",
                        alignItems: "center",
                        m: 0,
                        p: 0,
                      }}
                    >
                      Let's Login
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </RevealFade>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default UserRegister;
